/** @jsx isolateComponent */

import { useState } from 'react';

import { ensureDate } from '@zapier/date';
import { isolateComponent } from '@zapier/style-encapsulation';

import { UpgradeModal } from '../UpgradeModal';
import { useTaskLimitUpgradeNudges } from './useTaskLimitUpgradeNudges';

import { useIsAuthenticated } from '@zapier/upsells/src/hooks';

const HAS_SEEN_OUT_OF_TASKS_MODAL = 'hasSeenOutOfTasksModal';
const TASK_LIMIT_LOW_MODAL_LAST_SEEN = 'taskLimitLowModalLastSeen';

export const setHasSeenOutOfTasksModal = (value: boolean) =>
  localStorage.setItem(HAS_SEEN_OUT_OF_TASKS_MODAL, value.toString());

export const LoginTaskLimitUpgradeModal = () => {
  const { isAuthenticated } = useIsAuthenticated();
  return isAuthenticated ? <LoginTaskLimitUpgradeModalAuthed /> : null;
};

export const LoginTaskLimitUpgradeModalAuthed = () => {
  const {
    billingPeriodEndDate,
    billingPeriodStartDate,
    isEligibleForLowTaskUpgradeNudge,
    isEligibleForTaskUpgradeNudge,
    isLoading,
  } = useTaskLimitUpgradeNudges();

  const lastSawLowTasksModalString = localStorage.getItem(
    TASK_LIMIT_LOW_MODAL_LAST_SEEN
  );

  const lastSawLowTasksModal = lastSawLowTasksModalString
    ? ensureDate(lastSawLowTasksModalString)
    : undefined;

  const hasSeenLowTasksModalThisBillingPeriod = Boolean(
    lastSawLowTasksModal &&
      billingPeriodStartDate &&
      billingPeriodEndDate &&
      lastSawLowTasksModal.getTime() >= billingPeriodStartDate.getTime() &&
      lastSawLowTasksModal.getTime() <= billingPeriodEndDate.getTime()
  );

  const shouldShowModal = Boolean(
    isEligibleForLowTaskUpgradeNudge
      ? !hasSeenLowTasksModalThisBillingPeriod
      : isEligibleForTaskUpgradeNudge
      ? (localStorage.getItem(HAS_SEEN_OUT_OF_TASKS_MODAL) ?? 'false') ===
        'false'
      : false
  );

  return !isLoading ? (
    <LoginTaskLimitUpgradeModalStateful
      isEligibleForLowTaskUpgradeNudge={isEligibleForLowTaskUpgradeNudge}
      shouldShowModal={shouldShowModal}
    />
  ) : null;
};

type StatefulProps = {
  isEligibleForLowTaskUpgradeNudge: boolean;
  shouldShowModal: boolean;
};

const LoginTaskLimitUpgradeModalStateful = ({
  isEligibleForLowTaskUpgradeNudge,
  shouldShowModal,
}: StatefulProps) => {
  const setSawTaskLimitLowModal = () =>
    localStorage.setItem(TASK_LIMIT_LOW_MODAL_LAST_SEEN, new Date().toString());

  const [isModalVisible, setIsModalVisible] = useState(shouldShowModal);

  const onClose = () => {
    setIsModalVisible(false);
  };

  if (!isModalVisible) {
    return null;
  }

  if (isEligibleForLowTaskUpgradeNudge) {
    setSawTaskLimitLowModal();
  } else {
    setHasSeenOutOfTasksModal(true);
  }

  return (
    <UpgradeModal
      onClose={onClose}
      upgradeReason={isEligibleForLowTaskUpgradeNudge ? 'tasks-low' : 'tasks'}
    />
  );
};
